// Factory that binds dependencies at call-time and returns the async handler
export function createHandleSendMessage(deps) {
  return async function handleSendMessage(message) {
    const {
      messages,
      setMessages,
      imageAttachments,
      setImageAttachments,
      chatContexts,
      chatContext,
      rawAnalysisResponse,
      historyResult,
      extendedAnalysisResults,
      threadId,
      setThreadId,
      activeStream,
      setActiveStream,
      setShowTyping,
      setAnswerStarted,
      setHasStreamedContent,
      startAgentStream,
      openAgentStream,
      setStreamingToolMap,
      setStreamingToolOrder,
      originalMessages,
      setOriginalMessages,
      setTurnReasoningSections,
      setReasoningBuffer,
      hasStreamedContent,
      reasoningBuffer
    } = deps;

    try {
      setReasoningBuffer('');

      const buildReportContextString = () => {
        try {
          const overview =
            (rawAnalysisResponse?.result && rawAnalysisResponse.result[0]) ||
            (historyResult?.answer_data?.result && historyResult.answer_data.result[0]) ||
            null;
          const extended = extendedAnalysisResults || null;
          // Also allow a lightweight pending website context (name only) from floating input cookie
          let websiteContext = null;
          try {
            const getCookie = (name) => {
              const value = `; ${document.cookie}`;
              const parts = value.split(`; ${name}=`);
              if (parts.length === 2) return parts.pop().split(';').shift();
              return '';
            };
            const pendingCtx = getCookie('askpotPendingContextName');
            if (pendingCtx) {
              websiteContext = decodeURIComponent(pendingCtx);
              // Clear after first read so it does not affect next turns
              try {
                const domain = window.location.hostname === 'localhost' ? 'localhost' : '.askpot.com';
                document.cookie = `askpotPendingContextName=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=${domain};`;
              } catch (_) {
                document.cookie = 'askpotPendingContextName=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';
              }
            }
          } catch (_) {
            /* ignore cookie errors */
          }
          if (!overview && !extended && !websiteContext) return null;

          // Recursively strip large binary blobs (e.g., generated images) from the report
          const stripBlobs = (value) => {
            if (Array.isArray(value)) return value.map(stripBlobs);
            if (value && typeof value === 'object') {
              const out = {};
              for (const [k, v] of Object.entries(value)) {
                if (k === 'b64_image') continue; // drop base64 image payloads
                // Also drop obvious data URIs or extremely large strings to prevent overruns
                if (typeof v === 'string' && (v.length > 200000 || /^data:image\//.test(v))) continue;
                out[k] = stripBlobs(v);
              }
              return out;
            }
            return value;
          };

          const report = stripBlobs({
            overview,
            extended,
            website_context_name: websiteContext || undefined
          });
          return JSON.stringify(report);
        } catch (e) {
          return null;
        }
      };

      const buildSelectedContextString = () => {
        if (chatContexts && chatContexts.length > 0) {
          return chatContexts.map((ctx, idx) => `Context ${idx + 1} (${ctx.selectionType || 'card'}): "${ctx.content}"`).join('\n\n');
        }
        if (chatContext) return String(chatContext);
        return null;
      };

      const systemMessages = [];
      const reportContextText = buildReportContextString();
      if (reportContextText) {
        systemMessages.push({ role: 'system', content: `Report Context (full report):\n${reportContextText}` });
      }
      const selectedContextText = buildSelectedContextString();
      if (selectedContextText) {
        systemMessages.push({ role: 'system', content: `Selected Context (user highlighted):\n${selectedContextText}` });
      }

      const userMessage = message;
      const attachmentsSnapshot = (imageAttachments || []).slice();

      const newMessage = {
        id: Date.now(),
        type: 'user',
        content: userMessage,
        timestamp: Date.now(),
        attachments: imageAttachments && imageAttachments.length > 0 ? imageAttachments : undefined
      };

      setMessages((prev) => [...prev, newMessage]);
      // CRITICAL: Also add the new user message to originalMessages to maintain full history
      // originalMessages stores backend-format messages and is used to build the message history for the API
      setOriginalMessages((prev) => [
        ...prev,
        {
          role: 'user',
          content: userMessage,
          created_at: new Date().toISOString()
        }
      ]);
      if (attachmentsSnapshot.length > 0) {
        setImageAttachments([]);
      }

      setShowTyping(true);
      setAnswerStarted(false);
      setHasStreamedContent(false);

      // Build message history for backend
      // Priority: 1) originalMessages (if set), 2) sessionStorage (for public thread history), 3) messages state
      let messageHistory = [];

      // First check originalMessages (may be empty due to async React state updates)
      if (originalMessages && originalMessages.length > 0) {
        messageHistory = [...originalMessages];
      } else {
        // If originalMessages is empty, check sessionStorage for public thread history
        // This handles the case where handleSendMessage is called before React state updates
        let publicThreadHistory = null;
        if (!threadId) {
          try {
            const historyKey = sessionStorage.getItem('askpotUsePublicThreadHistory');
            if (historyKey) {
              const stored = sessionStorage.getItem(historyKey);
              if (stored) {
                const parsed = JSON.parse(stored);
                // Check if the stored history is recent (within last 30 minutes) to prevent stale data
                const isRecent = parsed.timestamp && Date.now() - parsed.timestamp < 30 * 60 * 1000;
                if (parsed.messageHistory && Array.isArray(parsed.messageHistory) && parsed.messageHistory.length > 0 && isRecent) {
                  publicThreadHistory = parsed.messageHistory;
                }
              }
            }
          } catch (e) {
            // ignore sessionStorage errors
          }
        }

        if (publicThreadHistory && publicThreadHistory.length > 0) {
          // Use public thread history from sessionStorage (already in backend format)
          messageHistory = [...publicThreadHistory];
        } else {
          // Convert from messages state (UI format: { type, content })
          messageHistory = messages.map((m) => ({
            role: m.type === 'user' ? 'user' : 'assistant',
            content: m.content
          }));
        }
      }

      // Add the new user message to the history
      messageHistory.push({
        role: 'user',
        content: userMessage
      });

      // Filter out Reddit Scraper tool results to prevent context overflow
      // Reddit Scraper tool results are too large and will cause the LLM context to exceed limits
      const filteredMessageHistory = messageHistory.filter((msg) => {
        if (msg.role !== 'tool') return true;
        const toolName = msg.metadata?.tool_name || msg.tool_name || '';
        const normalizedToolName = String(toolName).toLowerCase().trim();
        // Exclude reddit_scraper tool messages (case-insensitive matching)
        return normalizedToolName !== 'reddit_scraper' && normalizedToolName !== 'reddit scraper';
      });

      const attachmentMessages = attachmentsSnapshot.map((a) => ({
        role: 'user',
        content: `data:${a.type || 'image/png'};base64,${a.base64}`
      }));

      // Final history includes: system messages + conversation history (including public thread history if loaded) + attachments
      // Note: Reddit Scraper tool results are filtered out to prevent context overflow
      const finalHistory = [...systemMessages, ...filteredMessageHistory, ...attachmentMessages];
      const streamUrl = await startAgentStream({ content: userMessage, messages: finalHistory, threadId });

      if (!streamUrl) throw new Error('No stream URL returned');
      if (activeStream) activeStream.close();

      setMessages((prev) => {
        const last = prev[prev.length - 1];
        if (last && last.type === 'ai' && last.streaming) return prev;
        return [...prev, { id: Date.now() + 1, type: 'ai', content: '', timestamp: Date.now(), streaming: true }];
      });
      setAnswerStarted(true);

      const es = await openAgentStream(streamUrl, {
        onOpen: (info) => {
          if (info && info.thread_id && !threadId) setThreadId(info.thread_id);
        },
        onToken: (data) => {
          const tokenText = data?.text ?? data?.delta ?? data?.content ?? data?.message ?? '';
          // Handle structured errors (e.g., insufficient credits) during initial stream
          if (data && data.type === 'error') {
            const code = data.code || data.error || '';
            if (String(code).toLowerCase() === 'insufficient_credits') {
              setShowTyping(false);
              setHasStreamedContent(false);
              setMessages((prev) => {
                const last = prev[prev.length - 1];
                const paywallMsg = {
                  id: Date.now() + 4,
                  type: 'ai',
                  subtype: 'insufficient_credits',
                  timestamp: Date.now(),
                  meta: {
                    available_credits: data.available_credits,
                    required_credits: data.required_credits,
                    tool_name: data.tool_name,
                    user_plan: data.user_plan,
                    is_free_plan:
                      typeof data.is_free_plan === 'boolean'
                        ? data.is_free_plan
                        : String(data.user_plan || '')
                            .trim()
                            .toLowerCase() === 'free'
                  }
                };
                if (last && last.type === 'ai' && last.streaming) {
                  const updated = [...prev];
                  updated[updated.length - 1] = paywallMsg;
                  return updated;
                }
                return [...prev, paywallMsg];
              });
              return;
            }
          }
          if (
            data &&
            typeof data === 'object' &&
            (data.type === 'tool_started' || data.type === 'tool_output' || data.type === 'tool_completed')
          ) {
            const toolName = data.tool_name || data.metadata?.tool_name || 'unknown_tool';
            const toolCallId = data.metadata?.tool_call_id || `${toolName}-${Date.now()}`;
            const args = (() => {
              try {
                if (!data.args) return undefined;
                return typeof data.args === 'string' ? JSON.parse(data.args) : data.args;
              } catch {
                return data.args;
              }
            })();
            if (data.type === 'tool_started') {
              setStreamingToolMap((prev) => ({
                ...prev,
                [toolCallId]: {
                  ...(prev[toolCallId] || {}),
                  tool_name: toolName,
                  status: 'running',
                  args,
                  startTs: prev[toolCallId]?.startTs || Date.now(),
                  lastUpdateTs: Date.now()
                }
              }));
              setStreamingToolOrder((prev) => (prev.includes(toolCallId) ? prev : [...prev, toolCallId]));
            } else if (data.type === 'tool_output') {
              setStreamingToolMap((prev) => ({
                ...prev,
                [toolCallId]: {
                  ...(prev[toolCallId] || {}),
                  tool_name: toolName,
                  status: prev[toolCallId]?.status || 'running',
                  args: prev[toolCallId]?.args || args,
                  tool_result: data.result,
                  lastUpdateTs: Date.now()
                }
              }));
              // Also persist a tool message snapshot so inline "View" can open the viewer during streaming
              try {
                setOriginalMessages((prev) => [
                  ...prev,
                  {
                    role: 'tool',
                    content: '',
                    created_at: new Date().toISOString(),
                    metadata: { tool_name: toolName, args: (prev.find ? undefined : undefined) || undefined, tool_result: data.result }
                  }
                ]);
              } catch (_) {
                // ignore
              }
            } else if (data.type === 'tool_completed') {
              let finalResult = null;
              let finalArgs = args;
              setStreamingToolMap((prev) => {
                const existingEntry = prev[toolCallId] || {};
                finalResult = typeof existingEntry.tool_result !== 'undefined' ? existingEntry.tool_result : data.result ?? null;
                finalArgs = existingEntry.args || finalArgs;
                return {
                  ...prev,
                  [toolCallId]: {
                    ...existingEntry,
                    tool_name: toolName,
                    status: 'completed',
                    lastUpdateTs: Date.now()
                  }
                };
              });
              setOriginalMessages((prev) => [
                ...prev,
                {
                  role: 'tool',
                  content: '',
                  created_at: new Date().toISOString(),
                  metadata: {
                    tool_name: toolName,
                    args: finalArgs || undefined,
                    tool_result: finalResult || null
                  }
                }
              ]);
            }
            return;
          }
          const isReasoningToken = data?.type === 'reasoning';
          if (isReasoningToken) {
            if (!tokenText) return;
            setTurnReasoningSections((prev) => {
              const updated = [...prev];
              let idx = updated.findIndex((s) => s.afterMessageId === newMessage.id);
              if (idx === -1) {
                updated.push({
                  id: Date.now() + 200,
                  afterMessageId: newMessage.id,
                  steps: [],
                  raw: '',
                  titles: [],
                  scanIndex: 0,
                  open: false
                });
                idx = updated.length - 1;
              }
              const prevRaw = updated[idx].raw || '';
              const nextRaw = prevRaw + tokenText;
              const sliceStart = updated[idx].scanIndex || 0;
              const slice = nextRaw.substring(sliceStart);
              const titleSet = new Set(updated[idx].titles || []);
              const boldRe = /\*\*([^*]+)\*\*/g;
              let m;
              while ((m = boldRe.exec(slice)) !== null) {
                const t = String(m[1] || '').trim();
                if (t && t.length >= 3 && t.length <= 80) titleSet.add(t);
              }
              const headingRe = /(?:^|\n)\s{0,3}#{2,3}\s+(.+?)(?=\n|$)/g;
              while ((m = headingRe.exec(slice)) !== null) {
                const t = String(m[1] || '').trim();
                if (t && t.length >= 3 && t.length <= 80) titleSet.add(t);
              }
              updated[idx] = { ...updated[idx], raw: nextRaw, titles: Array.from(titleSet), scanIndex: nextRaw.length };
              return updated;
            });
            setReasoningBuffer((prev) => prev + tokenText);
            return;
          }
          if (tokenText) {
            setHasStreamedContent(true);
            if (!deps.answerStarted) {
              setAnswerStarted(true);
            }
            setMessages((prev) => {
              const last = prev[prev.length - 1];
              if (last && last.type === 'ai' && last.streaming) {
                const updated = [...prev];
                updated[updated.length - 1] = { ...last, content: (last.content || '') + tokenText };
                return updated;
              }
              return [...prev, { id: Date.now() + 1, type: 'ai', content: tokenText, timestamp: Date.now(), streaming: true }];
            });
          }
        },
        onComplete: (info) => {
          setShowTyping(false);
          const text = info?.lastText || '';
          setTurnReasoningSections((prev) => {
            const updated = [...prev];
            const idx = updated.findIndex((s) => s.afterMessageId === newMessage.id);
            const steps = Array.isArray(info?.final?.reasoning) ? info.final.reasoning : [];
            const raw = (info?.final?.reasoning_text || '').trim() || reasoningBuffer;
            const titles = steps.map((s) => s.title).filter(Boolean);
            if (idx === -1) {
              updated.push({
                id: Date.now() + 201,
                afterMessageId: newMessage.id,
                steps,
                raw,
                titles,
                scanIndex: raw.length,
                open: false,
                done: true
              });
            } else {
              updated[idx] = { ...updated[idx], steps, raw, titles, scanIndex: raw.length, done: true };
            }
            return updated;
          });
          setMessages((prev) => {
            const last = prev[prev.length - 1];
            if (last && last.type === 'ai' && last.streaming) {
              const updated = [...prev];
              const backendMessageId = info?.final?.message_id || info?.message_id || last.backendMessageId || null;
              updated[updated.length - 1] = { ...last, streaming: false, backendMessageId };
              return updated;
            }
            if (text) {
              const backendMessageId = info?.final?.message_id || info?.message_id || null;
              return [...prev, { id: Date.now() + 3, type: 'ai', content: text, timestamp: Date.now(), backendMessageId }];
            }
            return prev;
          });
          // CRITICAL: Also add the assistant message to originalMessages to maintain full history
          // This ensures the next request includes all previous messages
          setOriginalMessages((prev) => {
            // Get the final assistant content - either from the final payload or accumulated text
            const assistantContent = info?.final?.message || text || '';
            if (!assistantContent) return prev;
            return [
              ...prev,
              {
                role: 'assistant',
                content: assistantContent,
                created_at: new Date().toISOString(),
                ...(info?.final?.message_id && { id: info.final.message_id })
              }
            ];
          });
          setActiveStream(null);
        },
        onError: () => {
          setShowTyping(false);
          setActiveStream(null);
          if (!hasStreamedContent) {
            setMessages((prev) => [
              ...prev,
              { id: Date.now() + 2, type: 'ai', content: 'Stream ended without content.', timestamp: Date.now() }
            ]);
          }
        }
      });

      setActiveStream(es);
    } catch (err) {
      setShowTyping(false);
    }
  };
}
